import React, { useMemo, useState, useRef } from "react";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import { postData } from "../../helpers/url-request";
import { showLoading } from "react-global-loading";
import FullScreenWizardLayout from "../../components/Common/FullScreenWizardLayout";
import "./FirmCreateWizard.scss";

const steps = [
  {
    key: "details",
    title: "Firm Details",
    description: "Basic information about the firm",
  },
  {
    key: "authentication",
    title: "Authentication",
    description: "Choose SSO or PIERS login",
  },
  {
    key: "subscription",
    title: "Subscription",
    description: "Choose plan and billing",
  },
  {
    key: "review",
    title: "Review & Create",
    description: "Review and confirm",
  },
];

const initialValues = {
  name: "",
  firmCode: "",
  primaryContactName: "",
  emailId: "",
  contactNo: "",
  website: "",
  address: "",
  authMode: "local",
  providerName: "Microsoft Entra ID",
  issuer: "",
  clientId: "",
  clientSecret: "",
  passwordPolicy: "PIERS Default Policy",
  mfa: "optional",
  sessionTimeout: "30 Minutes",
  subscriptionType: "trial",
  plan: "PIERS Professional",
  billingCycle: "yearly",
  seats: "25",
  trialDuration: "30",
};

const emailIsValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const urlIsValid = (value) => !value || /^https?:\/\/.+/i.test(value);

const ChoiceCard = ({ active, icon, title, description, onClick, badge }) => (
  <button
    type="button"
    className={`firm-create-wizard__choice ${active ? "is-active" : ""}`}
    onClick={onClick}
  >
    <span className="firm-create-wizard__radio">
      <i />
    </span>
    <i className={`bx ${icon}`} />
    <span>
      <b>
        {title}
        {badge && <em>{badge}</em>}
      </b>
      <small>{description}</small>
    </span>
  </button>
);

const FirmCreateWizard = ({ closeAction }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const completion = useMemo(
    () => Math.round(((step + 1) / steps.length) * 100),
    [step]
  );
  const isSso = values.authMode === "sso";

  const update = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));
  };

  const validate = (currentStep) => {
    const nextErrors = {};
    if (currentStep === 0) {
      if (!values.name.trim())
        nextErrors.name = "Enter the firm name to continue.";
      if (!values.primaryContactName.trim())
        nextErrors.primaryContactName = "Enter the primary contact name.";
      if (!emailIsValid(values.emailId.trim()))
        nextErrors.emailId = "Enter a valid primary contact email.";
      if (!urlIsValid(values.website.trim()))
        nextErrors.website =
          "Use a full URL beginning with http:// or https://.";
    }
    if (currentStep === 1 && isSso) {
      if (!values.issuer.trim())
        nextErrors.issuer = "Enter the issuer (authority) URL.";
      if (!values.clientId.trim())
        nextErrors.clientId = "Enter the application client ID.";
      if (!values.clientSecret.trim())
        nextErrors.clientSecret = "Enter the client secret.";
    }
    if (
      currentStep === 2 &&
      (!Number.isInteger(Number(values.seats)) || Number(values.seats) < 1 || Number(values.seats) > 1000000)
    )
      nextErrors.seats = "Enter a whole number of licensed seats between 1 and 1,000,000.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const next = () => {
    if (validate(step))
      setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const back = () => setStep((current) => Math.max(current - 1, 0));

  const createFirm = () => {
    if (savingRef.current || ![0, 1, 2].every(validate)) return;
    savingRef.current = true;
    setSaving(true);
    const request = {
      id: 0,
      name: values.name.trim(),
      primaryContactName: values.primaryContactName.trim(),
      address: values.address.trim(),
      contactNo: values.contactNo.trim(),
      emailId: values.emailId.trim(),
      website: values.website.trim(),
      subscription: {
        subscriptionType: values.subscriptionType, plan: values.plan, seats: Number(values.seats),
        billingCycle: values.billingCycle, status: "active", revision: 0, notes: "",
        startsOn: new Date().toISOString().slice(0, 10),
        endsOn: values.subscriptionType === "trial" ? new Date(Date.now() + Number(values.trialDuration) * 86400000).toISOString().slice(0, 10) : null,
      },
    };
    showLoading(true);
    postData("/company/addupdate", request)
      .then((response) => {
        if (!response?.status)
          return ErrorNotify(response?.message || "Unable to create the firm.");
        SuccessNotify(
          "Firm and subscription created successfully."
        );
        closeAction?.();
      })
      .catch((error) => {
        ErrorNotify(error?.message || "Unable to create the firm.");
      })
      .finally(() => { showLoading(false); savingRef.current = false; setSaving(false); });
  };

  const field = (key, label, options = {}) => (
    <label
      className={`firm-create-wizard__field ${options.wide ? "is-wide" : ""}`}
      htmlFor={`firm-${key}`}
    >
      <span>
        {label}
        {options.required && <b>*</b>}
      </span>
      {options.type === "select" ? (
        <select
          id={`firm-${key}`}
          value={values[key]}
          onChange={(event) => update(key, event.target.value)}
        >
          {options.options.map(([value, text]) => (
            <option key={value} value={value}>
              {text}
            </option>
          ))}
        </select>
      ) : options.textarea ? (
        <textarea
          id={`firm-${key}`}
          rows="4"
          maxLength="500"
          value={values[key]}
          onChange={(event) => update(key, event.target.value)}
          placeholder={options.placeholder}
          aria-invalid={Boolean(errors[key])}
        />
      ) : (
        <input
          id={`firm-${key}`}
          type={options.type || "text"}
          value={values[key]}
          onChange={(event) => update(key, event.target.value)}
          placeholder={options.placeholder}
          aria-invalid={Boolean(errors[key])}
        />
      )}
      {options.helper && <small>{options.helper}</small>}
      {options.textarea && <em>{values[key].length} / 500</em>}
      {errors[key] && (
        <strong className="firm-create-wizard__error">{errors[key]}</strong>
      )}
    </label>
  );

  const details = (
    <>
      <header>
        <span className="firm-create-wizard__section-icon">
          <i className="bx bx-building-house" />
        </span>
        <div>
          <h2>Firm Details</h2>
          <p>
            Enter the profile and primary contact information for this firm.
          </p>
        </div>
      </header>
      <div className="firm-create-wizard__fields firm-create-wizard__fields--two">
        {field("name", "Firm Name", {
          required: true,
          placeholder: "Enter firm name",
          helper: "This will be the official name of the firm in PIERS.",
        })}
        {field("firmCode", "Firm Code (Optional)", {
          placeholder: "e.g. ACME",
          helper: "A short code to help identify the firm.",
        })}
        {field("primaryContactName", "Primary Contact Name", {
          required: true,
          placeholder: "Enter full name",
          helper: "The primary administrator or contact person.",
        })}
        {field("emailId", "Primary Contact Email", {
          required: true,
          type: "email",
          placeholder: "name@example.com",
          helper: "Used for important firm notifications.",
        })}
        {field("contactNo", "Phone Number (Optional)", {
          placeholder: "Enter phone number",
        })}
        {field("website", "Firm Website (Optional)", {
          placeholder: "https://www.example.com",
        })}
        {field("address", "Firm Address (Optional)", {
          wide: true,
          textarea: true,
          placeholder: "Enter firm address",
          helper: "The firm’s primary business address.",
        })}
      </div>
    </>
  );

  const authentication = (
    <>
      <header>
        <span className="firm-create-wizard__section-icon is-purple">
          <i className="bx bx-lock-alt" />
        </span>
        <div>
          <h2>Authentication</h2>
          <p>Choose how this firm’s users will sign into PIERS.</p>
        </div>
      </header>
      <div className="firm-create-wizard__preview-notice">
        <i className="bx bx-info-circle" />
        Authentication settings are being designed in this UI and will be
        connected to provisioning APIs later. PIERS currently enforces a
        30-minute inactivity timeout and a 10-hour absolute limit.
      </div>
      <div className="firm-create-wizard__choices">
        <ChoiceCard
          active={isSso}
          icon="bx-buildings"
          title="Single Sign-On (SSO)"
          description="Users sign in through the firm’s identity provider (OIDC)."
          onClick={() => update("authMode", "sso")}
        />
        <ChoiceCard
          active={!isSso}
          icon="bx-lock"
          title="Non SSO (PIERS Login)"
          description="Users sign in using their PIERS login and password."
          onClick={() => update("authMode", "local")}
        />
      </div>
      {isSso ? (
        <section className="firm-create-wizard__configuration">
          <h3>OIDC Configuration</h3>
          <p>Configure the OpenID Connect identity provider for this firm.</p>
          <div className="firm-create-wizard__fields firm-create-wizard__fields--two">
            {field("providerName", "Provider Name", {
              required: true,
              placeholder: "Microsoft Entra ID",
            })}
            {field("issuer", "Issuer (Authority)", {
              required: true,
              placeholder: "https://login.microsoftonline.com/tenant-id",
            })}
            {field("clientId", "Client ID", {
              required: true,
              placeholder: "Application (client) ID",
            })}
            {field("clientSecret", "Client Secret", {
              required: true,
              type: "password",
              placeholder: "Enter client secret",
            })}
          </div>
        </section>
      ) : (
        <section className="firm-create-wizard__configuration">
          <h3>PIERS Login Configuration</h3>
          <p>
            These defaults can be tailored for the firm after provisioning is
            available.
          </p>
          <div className="firm-create-wizard__fields firm-create-wizard__fields--two">
            {field("passwordPolicy", "Password Policy", {
              type: "select",
              options: [
                ["PIERS Default Policy", "PIERS Default Policy"],
                ["Strict Policy", "Strict Policy"],
                ["Custom Policy", "Custom Policy"],
              ],
            })}
            {field("mfa", "Multi-Factor Authentication", {
              type: "select",
              options: [
                ["optional", "Optional"],
                ["required", "Required"],
                ["disabled", "Disabled"],
              ],
            })}
            {field("sessionTimeout", "Idle Session Timeout", {
              type: "select",
              options: [
                ["30 Minutes", "30 Minutes (PIERS standard)"],
                ["15 Minutes", "15 Minutes"],
                ["60 Minutes", "60 Minutes"],
              ],
            })}
          </div>
        </section>
      )}
    </>
  );

  const subscription = (
    <>
      <header>
        <span className="firm-create-wizard__section-icon is-orange">
          <i className="bx bx-credit-card" />
        </span>
        <div>
          <h2>Subscription</h2>
          <p>Select the firm’s intended subscription and access settings.</p>
        </div>
      </header>
      <div className="firm-create-wizard__preview-notice">
        <i className="bx bx-info-circle" />
        Subscription terms will be saved with the firm. Payment collection is handled separately.
      </div>
      <div className="firm-create-wizard__choices firm-create-wizard__choices--three">
        <ChoiceCard
          active={values.subscriptionType === "trial"}
          icon="bx-flask"
          title="Trial"
          description="Give selected users access for a limited time."
          onClick={() => update("subscriptionType", "trial")}
        />
        <ChoiceCard
          active={values.subscriptionType === "paid"}
          icon="bx-credit-card"
          title="Paid Subscription"
          description="Choose a plan and billing cycle for full access."
          onClick={() => update("subscriptionType", "paid")}
        />
        <ChoiceCard
          active={values.subscriptionType === "complimentary"}
          icon="bx-gift"
          title="Complimentary"
          description="For internal teams or special arrangements."
          onClick={() => update("subscriptionType", "complimentary")}
        />
      </div>
      <section className="firm-create-wizard__configuration">
        <div className="firm-create-wizard__configuration-heading">
          <div>
            <h3>Plan &amp; Billing</h3>
            <p>Choose the intended access plan and billing cycle.</p>
          </div>
          <span>
            {values.subscriptionType === "trial"
              ? "30-day trial"
              : values.subscriptionType === "paid"
              ? "Paid plan"
              : "No payment"}
          </span>
        </div>
        <div className="firm-create-wizard__fields firm-create-wizard__fields--two">
          {field("plan", "Plan", {
            type: "select",
            options: [
              ["PIERS Professional", "PIERS Professional — up to 25 users"],
              ["PIERS Enterprise", "PIERS Enterprise — custom users"],
            ],
          })}
          {field("seats", "Licensed Users / Seats", {
            required: true,
            type: "number",
            helper: "Total users who will have access to PIERS.",
          })}
          {field("billingCycle", "Billing Cycle", {
            type: "select",
            options: [
              ["yearly", "Yearly"],
              ["monthly", "Monthly"],
            ],
          })}
          {values.subscriptionType === "trial"
            ? field("trialDuration", "Trial Duration", {
                type: "select",
                options: [
                  ["30", "30 Days"],
                  ["14", "14 Days"],
                  ["60", "60 Days"],
                ],
              })
            : null}

        </div>
      </section>
    </>
  );

  const review = (
    <>
      <header>
        <span className="firm-create-wizard__section-icon is-green">
          <i className="bx bx-check-shield" />
        </span>
        <div>
          <h2>Review &amp; Create</h2>
          <p>Confirm the firm profile and intended setup before creating it.</p>
        </div>
      </header>
      <dl className="firm-create-wizard__review">
        <div>
          <dt>Firm</dt>
          <dd>{values.name || "Not provided"}</dd>
        </div>
        <div>
          <dt>Primary Contact</dt>
          <dd>{values.primaryContactName || "Not provided"}</dd>
        </div>
        <div>
          <dt>Contact Email</dt>
          <dd>{values.emailId || "Not provided"}</dd>
        </div>
        <div>
          <dt>Authentication</dt>
          <dd>
            {isSso
              ? `SSO — ${values.providerName || "Provider not set"}`
              : "PIERS Login"}
          </dd>
        </div>
        <div>
          <dt>Subscription</dt>
          <dd>
            {values.subscriptionType === "trial"
              ? `${values.trialDuration}-day Trial`
              : values.subscriptionType === "paid"
              ? "Paid Subscription"
              : "Complimentary"}
          </dd>
        </div>
        <div>
          <dt>Plan &amp; Seats</dt>
          <dd>
            {values.plan} · {values.seats || 0} users
          </dd>
        </div>
      </dl>
      <aside className="firm-create-wizard__next">
        <h3>What happens next?</h3>
        <p>
          <b>1</b>The firm profile will be created and available in PIERS.
        </p>
        <p>
          <b>2</b>The subscription will be saved. Authentication settings remain a preview and are not provisioned here.
        </p>
      </aside>
    </>
  );

  const activeContent = [details, authentication, subscription, review][step];
  const guidance = (
    <>
      <section className="firm-create-wizard__guidance-card">
        <h2>
          <i className="bx bx-book-open" /> Guidance
        </h2>
        <div>
          <b>
            <i className="bx bx-info-circle" /> About this step
          </b>
          <p>
            {step === 0
              ? "Provide the firm profile and primary contact information. Required fields are marked with an asterisk."
              : step === 1
              ? "Select the sign-in approach that best suits this firm’s users."
              : step === 2
              ? "Choose the intended access plan and billing arrangement."
              : "Review the firm profile and planned configuration before creating it."}
          </p>
        </div>
        <ul>
          <li>
            <i className="bx bx-check-circle" />
            Use official firm information
          </li>
          <li>
            <i className="bx bx-check-circle" />
            Complete required fields
          </li>
          <li>
            <i className="bx bx-check-circle" />
            You can update profile details later
          </li>
        </ul>
      </section>
      <section className="firm-create-wizard__guided-tour">
        <i className="bx bx-compass" />
        <h3>New to firm setup?</h3>
        <p>Take a quick guided tour to learn this wizard.</p>
        <button type="button">
          <i className="bx bx-play" /> Start Guided Tour
        </button>
      </section>
      <section className="firm-create-wizard__need-help">
        <i className="bx bx-support" />
        <h3>Need more help?</h3>
        <p>Contact your administrator or support team.</p>
        <button type="button">Contact Administrator</button>
      </section>
    </>
  );
  const footer = (
    <>
      <div>
        <button
          type="button"
          className="firm-create-wizard__cancel"
          disabled={saving}
          onClick={closeAction}
        >
          Cancel
        </button>
        <button type="button" className="firm-create-wizard__save-draft">
          <i className="bx bx-save" /> Save Draft
        </button>
      </div>
      <div>
        {step > 0 && (
          <button
            type="button"
            className="full-screen-wizard__secondary-button"
            disabled={saving}
            onClick={back}
          >
            <i className="bx bx-left-arrow-alt" /> Previous
          </button>
        )}
        {step < steps.length - 1 ? (
          <button
            type="button"
            className="full-screen-wizard__next-button"
            onClick={next}
          >
            Next Step <i className="bx bx-right-arrow-alt" />
          </button>
        ) : (
          <button
            type="button"
            className="full-screen-wizard__next-button"
            disabled={saving}
            onClick={createFirm}
          >
            Create Firm <i className="bx bx-check" />
          </button>
        )}
      </div>
    </>
  );
  return (
    <FullScreenWizardLayout
      className="firm-create-wizard"
      title="Create Firm"
      subtitle="Set up the firm profile and prepare its PIERS configuration."
      steps={steps}
      activeStep={step}
      onStepChange={saving ? undefined : setStep}
      progress={completion}
      progressLabel="Completion"
      progressDetail={`Step ${step + 1} of ${steps.length}`}
      guidance={guidance}
      footer={footer}
      onClose={saving ? undefined : closeAction}
    >
      <section className="firm-create-wizard__workspace">
        <header>
          <h2>{step === 0 ? "Basic Information" : steps[step].title}</h2>
          <p>
            {step === 0
              ? "Provide the essential details to create the firm."
              : steps[step].description}
          </p>
        </header>
        <section className="firm-create-wizard__panel">{activeContent}</section>
      </section>
    </FullScreenWizardLayout>
  );
};

export default FirmCreateWizard;
